import React, { createContext, useContext, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import { apiQueryPrograms, apiSearchNotifications } from '../components/routes/observer-search/api';
import { apiSearchTriggers } from '../components/routes/triggers/api';
import { apiGetNotifications } from '../components/routes/notification-list/api';
import {
    downloadCsvFile,
    initObserverState,
    NotificationListItem,
    PaginationRequest,
    PaginationResponse,
    requestedField,
    requestedOrder
} from '../components/routes/observer-search/utils';

export const NotificationContext = createContext();

export const NotificationContextProvider = ( { children } ) => {
    const [ observerState, setObserverState ] = useState( initObserverState );

    const refreshSearchPage = companyId => {
        setObserverState( initObserverState );
        loadSearchOptions( companyId );
    };

    const loadSearchOptions = async companyId => {
        let programOptions = [];
        let triggerOptions = [];
        try {
            const programResults = await apiQueryPrograms( companyId );
            programOptions = programResults.map( ( program ) => ( {
                value: program.id,
                label: program.name,
                status: program.status
            } ) );
            const triggerResults = await apiSearchTriggers();
            triggerOptions = triggerResults.triggers.map( ( trigger ) => ( {
                value: trigger.id,
                label: trigger.internalName
            } ) );
        } finally {
            setObserverState( {
                ...initObserverState,
                companyId,
                searchOptions: {
                    programs: programOptions.sort( ( a, b ) => a.label.localeCompare( b.label ) ),
                    triggers: triggerOptions.sort( ( a, b ) => a.label.localeCompare( b.label ) )
                },
                pageIsLoading: false
            } );
        }
    };

    const loadNotificationOptions = async ( triggerKey ) => {
        const currentOptions = cloneDeep( observerState.searchOptions );
        if ( triggerKey === undefined ) {
            set( currentOptions, 'notifications', [] );
            setObserverState( {
                ...observerState,
                searchOptions: currentOptions,
                notificationsDisabled: true
            } );
        } else {
            setObserverState( { ...observerState, notificationOptionsLoading: true } );
            try {
                const notificationResults = await apiGetNotifications();
                const filteredResults = notificationResults.notifications.filter( ( item ) => {
                    return item.targetTrigger.id === triggerKey;
                } );
                const notificationOptions = filteredResults.map( ( notification ) => ( {
                    value: notification.id,
                    label: notification.name
                } ) );
                set( currentOptions, 'notifications', notificationOptions );
            } finally {
                setObserverState( {
                    ...observerState,
                    searchOptions: currentOptions,
                    notificationOptionsLoading: false,
                    notificationsDisabled: false
                } );
            }
        }
    };

    const onSearch = async searchValues => {
        const currentState = cloneDeep( observerState );
        setObserverState( {
            ...observerState,
            tableIsVisable: true,
            tableIsLoading: true
        } );
        try {
            const searchResults = await apiSearchNotifications( searchValues, currentState.companyId );
            set( currentState, 'notificationList', searchResults.lines );
            set( currentState, 'paginationInfo', searchResults.pagination );
        } finally {
            setObserverState( {
                ...observerState,
                searchValues,
                notificationList: currentState.notificationList.map(
                    ( listItem ) => new NotificationListItem( listItem )
                ),
                paginationInfo: new PaginationResponse( currentState.paginationInfo ),
                tableIsVisable: true,
                tableIsLoading: false,
                selectedNotifications: [] // re-setting this to [] after new search
            } );
        }
    };

    const onTableChange = async ( tableData, searchValues ) => {
        const currentState = cloneDeep( observerState );
        let paginationReq = new PaginationRequest();
        setObserverState( { ...observerState, tableIsLoading: true } );
        switch ( tableData.action ) {
            case 'sort':
                set( currentState, 'tableSort', {
                    field: tableData.sorter.columnKey,
                    direction: tableData.sorter.order
                } );
                paginationReq = new PaginationRequest( {
                    sortByField: requestedField( tableData.sorter.columnKey ),
                    sortByOrder: requestedOrder( tableData.sorter.order )
                } );
                break;
            case 'paginate':
                paginationReq = new PaginationRequest( {
                    pageNumber: tableData.pagination.current,
                    sortByField: requestedField( currentState.tableSort.field ),
                    sortByOrder: requestedOrder( currentState.tableSort.direction ),
                    pageSize: tableData.pagination.pageSize
                } );
                break;

            default:
                break;
        }

        try {
            const searchResults = await apiSearchNotifications(
                searchValues,
                currentState.companyId,
                paginationReq
            );
            set( currentState, 'notificationList', searchResults.lines );
            set( currentState, 'paginationInfo', searchResults.pagination );
        } finally {
            setObserverState( {
                ...currentState,
                notificationList: currentState.notificationList.map(
                    ( listItem ) => new NotificationListItem( listItem )
                ),
                paginationInfo: new PaginationResponse( currentState.paginationInfo ),
                tableIsLoading: false
            } );
        }
    };

    const onDownload = async searchValues => {
        const headers = [
            'Given Name,Surname,Email Address,Username/Login ID,Date Sent/Attempted,Notification Name,Status,Medium'
        ];

        setObserverState( { ...observerState, downloadIsLoading: true } );
        try {
            const paginationReq = new PaginationRequest( {
                pageSize: observerState.paginationInfo.totalElements,
                sortByField: observerState.paginationInfo.sortByField,
                sortByOrder: observerState.paginationInfo.sortByOrder
            } );
            const notificationRes = await apiSearchNotifications(
                searchValues,
                observerState.companyId,
                paginationReq
            );
            const notificationList = notificationRes.lines.map(
                ( listItem ) => new NotificationListItem( listItem )
            );
            const notificationsCsv = notificationList.reduce( ( acc, line ) => {
                acc.push(
                    [
                        line.givenName,
                        line.surname,
                        line.emailAddress,
                        line.loginId,
                        line.dateSent,
                        line.notification.name,
                        line.status,
                        line.medium
                    ].join( ',' )
                );
                return acc;
            }, [] );
            downloadCsvFile( [ ...headers, ...notificationsCsv ].join( '\n' ) );
        } finally {
            setObserverState( { ...observerState, downloadIsLoading: false } );
        }
    };

    return (
        <NotificationContext.Provider
            value={ {
                observerState,
                setObserverState,
                refreshSearchPage,
                loadNotificationOptions,
                onSearch,
                onTableChange,
                onDownload
            } }
        >
            { children }
        </NotificationContext.Provider>
    );
};

export const useNotificationContext = () => useContext( NotificationContext );